import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "isPassword", "showPassword", "togglePasswordVisible", "color", "extraMessage", "helperText", "className", "multiline", "value", "onChange", "onBlur", "InputProps", "inputProps", "icon", "showSecurityLevel", "passwordSecurityLevel", "getPasswordValidationResult", "mini", "label", "showCount", "loading", "theme", "InputLabelProps", "fullWidth", "required", "placeholder", "autoFocusByKeyboard", "data-testid"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useId } from '../utils/useId';
import TextField from '@mui/material/TextField';
import { inputTheme, InputContainer } from './inputStyles';
import { ThemeProvider } from '@mui/material/styles';
import { PasswordSecurity } from '../passwordSecurity/passwordSecurity';
import { EmbersStatusIcon } from '../statusIcon/statusIcon';
import { EmbersLoading } from '../loading/loading';
import { DarkStatus } from '../statusIcon/darkStatus/darkStatus';
import { useIntl } from 'react-intl';
import { useFieldFocusOnWindowBlur } from '../utils/useFieldFocusOnWindowBlur';
import { visuallyHidden } from '@mui/utils';
var DEFAULT_MAX_LENGTH = 500;
export var EmbersInput = function EmbersInput(props) {
  var _id = props.id,
    isPassword = props.isPassword,
    showPassword = props.showPassword,
    togglePasswordVisible = props.togglePasswordVisible,
    color = props.color,
    extraMessage = props.extraMessage,
    helperText = props.helperText,
    className = props.className,
    multiline = props.multiline,
    _props$value = props.value,
    _value = _props$value === void 0 ? '' : _props$value,
    onChange = props.onChange,
    onBlur = props.onBlur,
    InputProps = props.InputProps,
    inputProps = props.inputProps,
    icon = props.icon,
    showSecurityLevel = props.showSecurityLevel,
    passwordSecurityLevel = props.passwordSecurityLevel,
    getPasswordValidationResult = props.getPasswordValidationResult,
    mini = props.mini,
    label = props.label,
    _props$showCount = props.showCount,
    showCount = _props$showCount === void 0 ? true : _props$showCount,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    _props$theme = props.theme,
    theme = _props$theme === void 0 ? 'light' : _props$theme,
    InputLabelProps = props.InputLabelProps,
    fullWidth = props.fullWidth,
    required = props.required,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? '' : _props$placeholder,
    autoFocusByKeyboard = props.autoFocusByKeyboard,
    _props$dataTestid = props['data-testid'],
    dataTestId = _props$dataTestid === void 0 ? 'embers-input' : _props$dataTestid,
    otherProps = _objectWithoutProperties(props, _excluded);
  var id = useId(_id);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    clickFocus = _useState2[0],
    setClickFocus = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    inputHasFocus = _useState4[0],
    setInputHasFocus = _useState4[1];
  var _useState5 = useState(showPassword || false),
    _useState6 = _slicedToArray(_useState5, 2),
    passwordVisible = _useState6[0],
    setPasswordVisible = _useState6[1];
  var _useState7 = useState(_value),
    _useState8 = _slicedToArray(_useState7, 2),
    value = _useState8[0],
    setValue = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    hasAutofillValue = _useState10[0],
    setHasAutofillValue = _useState10[1];
  var intl = useIntl();
  useEffect(function () {
    if (otherProps.autoFocus && !autoFocusByKeyboard) {
      setClickFocus(true);
    }
  }, [otherProps.autoFocus, autoFocusByKeyboard]);
  useEffect(function () {
    setValue(_value ? _value.toString() : '');
  }, [_value]);
  useEffect(function () {
    if (showPassword !== undefined) {
      setPasswordVisible(showPassword);
    }
  }, [showPassword]);
  var hadClickFocusOnInputBlurRef = useFieldFocusOnWindowBlur(setClickFocus);
  var onInputChange = function onInputChange(e) {
    setValue(e.target.value);
    onChange && onChange(e);
  };
  var renderInputStatus = function renderInputStatus() {
    if (typeof extraMessage !== 'string') {
      return extraMessage;
    }
    if (color === 'success') {
      if (theme === 'dark') return /*#__PURE__*/React.createElement(DarkStatus, {
        type: color
      }, extraMessage);else return /*#__PURE__*/React.createElement(EmbersStatusIcon, {
        type: "success",
        iconName: "icon-check",
        size: "small"
      }, extraMessage);
    } else if (color === 'error') {
      if (theme === 'dark') return /*#__PURE__*/React.createElement(DarkStatus, {
        type: color
      }, extraMessage);else return /*#__PURE__*/React.createElement(EmbersStatusIcon, {
        type: "error",
        variant: "filled",
        size: "small"
      }, extraMessage);
    } else {
      return /*#__PURE__*/React.createElement("div", null, extraMessage);
    }
  };
  var renderHelperText = function renderHelperText() {
    return /*#__PURE__*/React.createElement("div", {
      id: "EmbersInput-helperText-".concat(id),
      className: classnames({
        'EmbersInput-helperText-root': true,
        'EmbersInput-helperText-dark': theme === 'dark'
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: "EmbersInput-helperText-content"
    }, extraMessage && /*#__PURE__*/React.createElement("div", {
      "aria-atomic": "true",
      "aria-live": "polite"
    }, renderInputStatus()), helperText, isPassword && showSecurityLevel && /*#__PURE__*/React.createElement(PasswordSecurity, {
      password: value,
      level: passwordSecurityLevel,
      callback: getPasswordValidationResult
    })), multiline && showCount && /*#__PURE__*/React.createElement("div", {
      className: "EmbersInput-helperText-multilineCharacterCount"
    }, value.length, "/", (inputProps === null || inputProps === void 0 ? void 0 : inputProps.maxLength) || DEFAULT_MAX_LENGTH));
  };
  var onPasswordVisibleChange = function onPasswordVisibleChange() {
    setPasswordVisible(!passwordVisible);
    togglePasswordVisible && togglePasswordVisible();
  };
  var handleEyeIconKeyDown = function handleEyeIconKeyDown(e) {
    switch (e.key) {
      case 'Enter':
        e.stopPropagation();
        onPasswordVisibleChange();
        break;
      default:
        break;
    }
  };
  var handleClick = function handleClick() {
    setClickFocus(true);
  };
  var handleBlur = function handleBlur(fEvt) {
    hadClickFocusOnInputBlurRef.current = clickFocus;
    setClickFocus(false);
    setInputHasFocus(false);
    if (onBlur) {
      onBlur(fEvt);
    }
  };
  var handleInputFocused = function handleInputFocused(fEvt) {
    setInputHasFocus(true);
  };
  var adornmentIcon;
  if (loading) {
    // ToDo: spike to figure out how to get aria-live and/or aria-busy to update
    adornmentIcon = /*#__PURE__*/React.createElement(EmbersLoading, {
      size: 'small'
    });
  } else {
    if (isPassword) {
      var iconClassName = classnames({
        'icon-visibility': !passwordVisible,
        'icon-visibility-off': passwordVisible
      });
      adornmentIcon = /*#__PURE__*/React.createElement("span", {
        role: "button",
        tabIndex: 0,
        "aria-label": iconClassName === 'icon-visibility' ? intl.formatMessage({
          id: 'embers.input.iconLabel.showPassword',
          defaultMessage: 'Show password'
        }) : intl.formatMessage({
          id: 'embers.input.iconLabel.hidePassword',
          defaultMessage: 'Hide password'
        }),
        className: iconClassName,
        onKeyDown: handleEyeIconKeyDown,
        onClick: onPasswordVisibleChange
      });
    } else {
      adornmentIcon = _typeof(icon) === 'object' ? icon : mini && (color === 'success' || color === 'error') ? /*#__PURE__*/React.createElement("div", {
        "aria-atomic": "true",
        "aria-live": "polite"
      }, /*#__PURE__*/React.createElement(EmbersStatusIcon, {
        id: "EmbersInput-miniIcon-".concat(id),
        type: color,
        variant: "filled"
      })) : null;
    }
  }

  // when used in the search component, the input label should not
  //  shrink up to the top.  The label needs to be displayed as
  //  a placeholder in this case
  var placeholderText = placeholder;
  var labelNode;
  if (otherProps.name === 'search') {
    if (typeof label === 'string') {
      placeholderText = label;
    }
  } else if (required) {
    labelNode = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, label), /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true",
      className: "EmbersInput-label-requiredPostfix"
    }, "\xA0*"));
  } else {
    labelNode = mini ? /*#__PURE__*/React.createElement("span", {
      style: visuallyHidden
    }, label) : label;
  }

  // hacky way to fix Chrome autofill will overlap with Input label
  // Mui bug: https://github.com/mui/material-ui/issues/36448, probably introduced in 5.12.x+
  var makeAnimationStartHandler = function makeAnimationStartHandler(stateSetter) {
    return function (e) {
      var _e$target;
      var autoFilled = !!((_e$target = e.target) !== null && _e$target !== void 0 && _e$target.matches('*:-webkit-autofill'));
      if (e.animationName === 'mui-auto-fill') {
        stateSetter(autoFilled);
      }
      if (e.animationName === 'mui-auto-fill-cancel') {
        stateSetter(autoFilled);
      }
    };
  };

  // Those fields are used to determine if aria-labelled by should include ids of helper text and mini icon
  // They collect all the conditions that decide if the element with right id is present in the DOM or not
  var hasHelperText = helperText || extraMessage || multiline && showCount;
  var hasMiniIcon = !isPassword && _typeof(icon) !== 'object' && mini && (color === 'success' || color === 'error');
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: inputTheme
  }, /*#__PURE__*/React.createElement(InputContainer, {
    "data-testid": dataTestId,
    className: classnames('EmbersInput-root', {
      'EmbersInput-fullWidth': fullWidth,
      'EmbersInput-required': required
    }, className)
  }, /*#__PURE__*/React.createElement(TextField, _extends({
    variant: theme === 'light' ? 'outlined' : 'filled',
    className: classnames("EmbersInput-".concat(theme), _defineProperty({
      'EmbersInput-mini': mini,
      'EmbersInput-endAdornment': (InputProps === null || InputProps === void 0 ? void 0 : InputProps.endAdornment) || isPassword,
      'EmbersInput-clickFocus': clickFocus,
      'EmbersInput-hasFocus': inputHasFocus,
      'EmbersInput-hasLabel': !!labelNode,
      'EmbersInput-hasInput': !!value
    }, "EmbersInput-".concat(color), color)),
    type: isPassword && !passwordVisible ? 'password' : otherProps.type,
    color: color,
    value: value,
    onChange: onInputChange,
    multiline: multiline,
    rows: multiline ? 4 : 1,
    helperText: hasHelperText && renderHelperText(),
    InputProps: _objectSpread(_objectSpread({}, InputProps), {}, {
      endAdornment: adornmentIcon || (InputProps === null || InputProps === void 0 ? void 0 : InputProps.endAdornment)
    }),
    inputProps: _objectSpread({
      maxLength: DEFAULT_MAX_LENGTH,
      readOnly: void 0,
      onFocus: handleInputFocused,
      'aria-required': !!required,
      'aria-busy': loading,
      'aria-labelledby': "EmbersInputText-".concat(id, " ").concat(hasMiniIcon ? "EmbersInput-miniIcon-".concat(id) : '', " ").concat(hasHelperText ? "EmbersInput-helperText-".concat(id) : '').trim(),
      'aria-invalid': color === 'error',
      required: !!required,
      onAnimationStart: makeAnimationStartHandler(setHasAutofillValue),
      onKeyDown: function onKeyDown(e) {
        if (!multiline && e.key === 'Enter') {
          onBlur === null || onBlur === void 0 || onBlur(e);
        }
      }
    }, inputProps),
    label: labelNode,
    placeholder: placeholderText,
    onMouseDown: handleClick,
    onBlur: handleBlur,
    InputLabelProps: _objectSpread(_objectSpread({
      id: "EmbersInputText-".concat(id),
      shrink: inputHasFocus || hasAutofillValue || !!value
    }, inputProps !== null && inputProps !== void 0 && inputProps.id ? {
      htmlFor: inputProps === null || inputProps === void 0 ? void 0 : inputProps.id
    } : {}), theme === 'dark' ? _objectSpread(_objectSpread({}, InputLabelProps), {}, {
      className: classnames(InputLabelProps === null || InputLabelProps === void 0 ? void 0 : InputLabelProps.className, 'EmbersInput-label-dark')
    }) : InputLabelProps),
    fullWidth: fullWidth
  }, otherProps)), (color === 'error' || color === 'success') && theme !== 'dark' && !mini && inputHasFocus && !clickFocus && /*#__PURE__*/React.createElement("fieldset", {
    id: "EmbersInput-".concat(id),
    className: classnames('EmbersInput-notchedOutline', {
      'EmbersInput-notchedOutline-multiline': multiline
    })
  }, /*#__PURE__*/React.createElement("legend", null, /*#__PURE__*/React.createElement("span", null, labelNode)))));
};